// Shared by both forks.
import React, { useMemo } from 'react';
import { SearchModeContext, SearchModeContextValue } from './SearchModeContext';
import { SearchContext, SearchContextValue } from './SearchContext';
import { SearchScopeContext, SearchScopeContextValue } from './SearchScopeContext';
import {
	SpotlightDropdownContext,
	SpotlightDropdownContextValue,
} from './SpotlightDropdownContext';

type Props = {
	mode: SearchModeContextValue;
	dropdown: SpotlightDropdownContextValue;
	search: SearchContextValue;
	scope: SearchScopeContextValue;
	children: React.ReactNode;
};

// One provider per Spotlight root: guidelines pages can mount two roots at once.
export function SpotlightStateProvider({ mode, dropdown, search, scope, children }: Props) {
	const {
		canUseSimilaritySearch,
		isNLSSearchOn,
		setIsNLSSearchOn,
		isPerformingNLSSearch,
		isHybridSearchEnabled,
		isHybridSearchOn,
		isDropdownSearchDisabled,
		isSearchOptionsDisabled,
	} = mode;

	const modeValue = useMemo(
		() => ({
			canUseSimilaritySearch,
			isNLSSearchOn,
			setIsNLSSearchOn,
			isPerformingNLSSearch,
			isHybridSearchEnabled,
			isHybridSearchOn,
			isDropdownSearchDisabled,
			isSearchOptionsDisabled,
		}),
		[
			canUseSimilaritySearch,
			isNLSSearchOn,
			setIsNLSSearchOn,
			isPerformingNLSSearch,
			isHybridSearchEnabled,
			isHybridSearchOn,
			isDropdownSearchDisabled,
			isSearchOptionsDisabled,
		],
	);

	const {
		isDropdownOpen,
		openSpotlightDropdown,
		closeSpotlightDropdown,
		refInput,
		openSearchByImage,
	} = dropdown;

	const dropdownValue = useMemo(
		() => ({
			isDropdownOpen,
			openSpotlightDropdown,
			closeSpotlightDropdown,
			refInput,
			openSearchByImage,
		}),
		[
			isDropdownOpen,
			openSpotlightDropdown,
			closeSpotlightDropdown,
			refInput,
			openSearchByImage,
		],
	);

	const {
		searchTerm,
		setSearchTerm,
		onValueChange,
		isLoadingResults,
		searchResults,
		clearResults,
		onClearButtonClick,
		defaultSearchAction,
		recentSearchTerms,
		addSearchTermToRecentSearches,
	} = search;

	const searchValue = useMemo(
		() => ({
			searchTerm,
			setSearchTerm,
			onValueChange,
			isLoadingResults,
			searchResults,
			clearResults,
			onClearButtonClick,
			defaultSearchAction,
			recentSearchTerms,
			addSearchTermToRecentSearches,
		}),
		[
			searchTerm,
			setSearchTerm,
			onValueChange,
			isLoadingResults,
			searchResults,
			clearResults,
			onClearButtonClick,
			defaultSearchAction,
			recentSearchTerms,
			addSearchTermToRecentSearches,
		],
	);

	const {
		selectedSearchOptions,
		setSelectedSearchOptions,
		removeSelectedSearchOption,
		selectedResultsCategory,
		setSelectedResultsCategory,
	} = scope;

	const scopeValue = useMemo(
		() => ({
			selectedSearchOptions,
			setSelectedSearchOptions,
			removeSelectedSearchOption,
			selectedResultsCategory,
			setSelectedResultsCategory,
		}),
		[
			selectedSearchOptions,
			setSelectedSearchOptions,
			removeSelectedSearchOption,
			selectedResultsCategory,
			setSelectedResultsCategory,
		],
	);

	return (
		<SearchModeContext.Provider value={modeValue}>
			<SpotlightDropdownContext.Provider value={dropdownValue}>
				<SearchContext.Provider value={searchValue}>
					<SearchScopeContext.Provider value={scopeValue}>
						{children}
					</SearchScopeContext.Provider>
				</SearchContext.Provider>
			</SpotlightDropdownContext.Provider>
		</SearchModeContext.Provider>
	);
}
